import { useMutation } from '@tanstack/react-query';

import {
    FidelityAccountList,
    FidelityApplicationSection,
    FidelityCompletionRequirements,
    FidelityDownloadButton,
    FidelityDownloadFeedback,
    FidelityPacketCard,
} from '@/components/fidelity/FidelityApplicationComponents';
import { CustomerApi } from 'API';

import { accountTitle } from './account-model';
import { FidelitySmaSectionProps } from './useFidelitySmaApplications';

export const FidelityInheritedIraApplicationView = ({
    household,
    applications,
    action,
    ssnAccess,
    checkingAccess,
}: FidelitySmaSectionProps<'inheritedIra'>) => {
    const title = (guid: string) => {
        const row = household.accounts.find(({ account }) => account.accountGUID === guid);
        return row ? accountTitle(row.account) : 'Account no longer available';
    };
    return (
        <FidelityApplicationSection
            name="Inherited IRA — Individuals"
            title="Inherited IRA Individuals Applications"
            hasAccounts={household.accounts.length > 0}
            groupCount={applications.data?.accountGUIDs.length}
            checkingAccess={checkingAccess}
            ssnAccess={ssnAccess}
            applications={applications}
        >
            <>
                {applications.data?.accountGUIDs.map((guid) => (
                    <InheritedIraDraft
                        key={guid}
                        accountGUID={guid}
                        title={title}
                        action={action}
                        onRefresh={() => void applications.refetch()}
                    />
                ))}
                {!!applications.data?.accountGUIDs.length && (
                    <FidelityCompletionRequirements requirements={applications.data.completionRequirements} />
                )}
            </>
        </FidelityApplicationSection>
    );
};

const InheritedIraDraft = ({
    accountGUID,
    title,
    action,
    onRefresh,
}: {
    accountGUID: string;
    title: (guid: string) => string;
    action: FidelitySmaSectionProps<'inheritedIra'>['action'];
    onRefresh: () => void;
}) => {
    const generate = useMutation({
        mutationFn: () => CustomerApi({ hideError: true }).getInheritedIraForm({ accountGUID }),
    });
    return (
        <FidelityPacketCard
            name="Inherited IRA — Individuals"
            title="Inherited IRA Individuals Application"
            accountCount={1}
            action={
                <FidelityDownloadButton download={generate} variant="outline" onClick={() => generate.mutate()}>
                    Generate Inherited IRA Individuals Application
                </FidelityDownloadButton>
            }
        >
            <FidelityAccountList accountGUIDs={[accountGUID]} title={title} action={action} />
            <FidelityDownloadFeedback
                download={generate}
                onRefresh={() => {
                    generate.reset();
                    onRefresh();
                }}
            />
        </FidelityPacketCard>
    );
};
